import { useContext, useEffect, useState } from 'react';
import { useQboApi } from '../../../clients/quipli-service/qbo/qbo-api-v1';
import { QBOCreditMemo, QBOInvoice } from '../../../clients/quipli-service/qbo/qbo';
import { displayMoneyFormat } from '../../../util/util';
import { TenantContext } from '../../../context/tenant-context';
import { Button } from '../../../components/form/button';
import { toast } from '../../../util/toasts';
import { OrderSection } from '../../order/order-section/order-section';
import { Typography } from '../../../components/typography/typography';
import { DataTable } from '../../../components/table/data-table';
import classNames from 'classnames';
import { isMobileOnly } from 'react-device-detect';

export const QboInvoice = (params: { orderId: number } | { workOrderId: string }) => {
  const { contextPath, configuration } = useContext(TenantContext);
  const [isSyncingInvoice, setIsSyncingInvoice] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<QBOInvoice[]>();
  const [creditMemos, setCreditMemos] = useState<QBOCreditMemo[]>();
  const { getQboInvoices, syncInvoice, getQboCreditMemos } = useQboApi();
  let qboEnabled = false;

  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }

  async function fetchInvoices(): Promise<void> {
    await getQboInvoices(params)
      .then((i) => {
        setInvoices(i);
      })
      .catch((e) => {
        console.error(`Error loading QuickBooks invoices`, e);
        toast(`Error loading QuickBooks invoices`, 'error');
      });

    await getQboCreditMemos(params)
      .then((i) => {
        setCreditMemos(i);
      })
      .catch((e) => {
        console.error(`Error loading QuickBooks credit memos`, e);
        toast(`Error loading QuickBooks credit memos`, 'error');
      });
  }

  useEffect(() => {
    if (qboEnabled) {
      fetchInvoices();
    }
  }, [configuration?.qboEnabled]);
  // @hipaa-exclude-end

  async function triggerInvoiceSync() {
    setIsSyncingInvoice(true);
    try {
      await syncInvoice(params);
      await getQboInvoices(params).then(setInvoices);
      await getQboCreditMemos(params).then(setCreditMemos);
      toast('Invoice synced successfully');
    } catch (ex: any) {
      toast(ex.message, 'error');
    }
    setIsSyncingInvoice(false);
  }

  return (
    <>
      {qboEnabled && (
        <OrderSection
          header={
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                QBO Invoice
              </Typography>
              <Button className="button button-secondary" disabled={isSyncingInvoice} onClick={triggerInvoiceSync}>
                Sync Invoice
              </Button>
            </div>
          }
          className="flex flex-col gap-[8px]"
        >
          <div
            className={classNames({
              'border border-solid border-grey-200 rounded-[9px]': !isMobileOnly,
            })}
          >
            <DataTable
              columnNames={[
                { key: 'date', label: 'Date' },
                { key: 'invoiceId', label: 'Invoice ID', showRule: 'OnlyDesktop' },
                { key: 'amount', label: 'Amount' },
                { key: 'emailSent', label: 'Email Sent' },
                { key: 'download', label: '' },
              ]}
              data={
                invoices?.length
                  ? invoices.map((invoice) => ({
                      mobileHeader: (
                        <>
                          <Typography type="text-md" weight="medium">
                            Invoice ID
                          </Typography>
                          <Typography type="text-sm" weight="regular" className="text-grey-500">
                            {`invoice_${invoice.Id}`}
                          </Typography>
                        </>
                      ),
                      fields: {
                        date: invoice.TxnDate,
                        invoiceId: `invoice_${invoice.Id}`,
                        amount: (
                          <>
                            <Typography type="text-sm" className="text-quipli-black-800">
                              {displayMoneyFormat(invoice.TotalAmt, configuration?.currency)}
                            </Typography>
                            <Typography type="text-sm" className="text-quipli-black-800">
                              {displayMoneyFormat(invoice.Balance, configuration?.currency)}
                            </Typography>
                          </>
                        ),
                        emailSent: invoice.EmailStatus === 'EmailSent' ? 'Yes' : 'No',
                        download: (
                          <Button
                            className={classNames('button-primary', {
                              'w-full': isMobileOnly,
                            })}
                          >
                            <a
                              target="_blank"
                              href={`${
                                contextPath ? contextPath : '/'
                              }quipli-service/v1/qbo/getInvoiceAsPdf?invoiceId=${invoice.Id}`}
                              title="Download Invoice"
                            >
                              <Typography type="text-sm" className="text-quipli-white">
                                Download
                              </Typography>
                            </a>
                          </Button>
                        ),
                      },
                    }))
                  : creditMemos?.length
                  ? creditMemos.map((creditMemo) => ({
                      mobileHeader: (
                        <>
                          <Typography type="text-md" weight="medium">
                            Credit Memo ID
                          </Typography>
                          <Typography type="text-sm" weight="regular" className="text-grey-500">
                            {`creditmemo_${creditMemo.Id}`}
                          </Typography>
                        </>
                      ),
                      fields: {
                        date: creditMemo.TxnDate,
                        invoiceId: `creditmemo_${creditMemo.Id}`,
                        amount: (
                          <>
                            <Typography type="text-sm" className="text-quipli-black-800">
                              {displayMoneyFormat(creditMemo.TotalAmt, configuration?.currency)}
                            </Typography>
                            <Typography type="text-sm" className="text-quipli-black-800">
                              {displayMoneyFormat(creditMemo.Balance, configuration?.currency)}
                            </Typography>
                          </>
                        ),
                        emailSent: creditMemo.EmailStatus === 'EmailSent' ? 'Yes' : 'No',
                        download: (
                          <Button
                            className={classNames('button-primary', {
                              'w-full': isMobileOnly,
                            })}
                          >
                            <a
                              target="_blank"
                              href={`${
                                contextPath ? contextPath : '/'
                              }quipli-service/v1/qbo/getCreditMemoAsPdf?creditMemoId=${creditMemo.Id}`}
                              title="Download Invoice"
                            >
                              <Typography type="text-sm" className="text-quipli-white">
                                Download
                              </Typography>
                            </a>
                          </Button>
                        ),
                      },
                    }))
                  : []
              }
            />
          </div>
        </OrderSection>
      )}
    </>
  );
};
